// frontend/src/components/p2p/OrderRow.tsx
import { useEffect, useMemo, useState } from 'react'
import { Clock } from 'lucide-react'
import { P2POrder } from '../../lib/api'
import { cn } from '../../lib/utils'
import { CancelOrderButton } from './CancelOrderButton'
import { SupportedChain, Timestamp, TxLine } from './tx-helpers'

interface Props {
  order: P2POrder
  onCancel?: (orderId: string) => void
  cancelPending?: boolean
}

export function OrderRow({ order, onCancel, cancelPending }: Props) {
  const fillPct = useMemo(() => {
    const amt = BigInt(order.amount)
    if (amt === 0n) return 0
    return Number((BigInt(order.filled) * 10000n) / amt) / 100
  }, [order.amount, order.filled])

  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (order.status !== 'ACTIVE' && order.status !== 'PARTIAL') return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [order.status])
  const expiresMs = new Date(order.expiresAt).getTime() - now

  const canCancel = !!onCancel && (order.status === 'ACTIVE' || order.status === 'PARTIAL')
  const isSellW2 = order.side === 'SELL_W2USDT'
  const tokenSold = isSellW2 ? 'w2usdt' : 'USDT'
  const chain = order.chain === 'UNI2' ? 'Uni2' : 'BSC'

  return (
    <div className="group rounded-xl border border-dark-800 bg-dark-900/40 hover:border-dark-700 transition-colors p-4">
      {/* Top row: side badge + id + status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SideBadge side={order.side} />
          <span className="font-mono text-[11px] text-dark-500 hidden sm:inline" title={order.orderId}>
            {shortenId(order.orderId)}
          </span>
        </div>
        <StatusPill status={order.status} expiresAt={order.expiresAt} />
      </div>

      {/* Amount + premium */}
      <div className="flex items-baseline justify-between mb-2">
        <div className="space-y-0.5">
          <div className="font-display text-2xl font-medium text-white tabular-nums">
            {formatHuman(order.filled)} <span className="text-dark-500">/</span> {formatHuman(order.amount)}
          </div>
          <div className="text-[11px] font-mono text-dark-500 uppercase tracking-wider">
            {tokenSold} en {chain}
          </div>
        </div>
        <div className="text-right">
          <div className={cn('font-mono text-lg', order.premiumBps >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
            {order.premiumBps >= 0 ? '+' : ''}
            {(order.premiumBps / 100).toFixed(2)}%
          </div>
          <div className="text-[11px] font-mono text-dark-500 uppercase tracking-wider">Prima</div>
        </div>
      </div>

      {/* Fill bar */}
      <div className="h-1.5 bg-dark-800/80 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-uni-600 to-uni-400 transition-all duration-500"
          style={{ width: `${fillPct}%` }}
        />
      </div>

      {/* Single tx hash: the createOrder tx that put the order on-chain. The
         closing tx (cancel/expire/fill) lives in the trade row + admin tools;
         doubling it up here just clutters the row. */}
      <div className="mt-3">
        <TxLine chain={chain as SupportedChain} hash={order.createdTxHash} />
      </div>

      {/* Bottom: timestamps + expires countdown + cancel */}
      <div className="flex items-end justify-between gap-3 mt-3 pt-2 border-t border-dark-800/60">
        <div className="flex flex-col gap-0.5 text-[11px] font-mono text-dark-500">
          <Timestamp label="Creada" iso={order.createdAt} />
          {order.closedTxHash ? (
            <Timestamp label="Cerrada" iso={order.updatedAt} />
          ) : (
            <span className={cn('flex items-center gap-1', expiresMs <= 0 && 'text-amber-400')}>
              <Clock className="w-3 h-3" />
              {expiresMs > 0
                ? `Expira en ${formatDuration(expiresMs)}`
                : 'Expirando…'}
            </span>
          )}
        </div>
        {canCancel && (
          <CancelOrderButton
            orderId={order.orderId}
            onCancel={onCancel!}
            pending={cancelPending ?? false}
          />
        )}
      </div>
    </div>
  )
}

function SideBadge({ side }: { side: P2POrder['side'] }) {
  const isW2 = side === 'SELL_W2USDT'
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border tracking-wide',
        isW2
          ? 'bg-rose-500/10 text-rose-400 border-rose-500/30'
          : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
      )}
    >
      {isW2 ? 'Vende w2usdt' : 'Vende USDT'}
    </span>
  )
}

function StatusPill({
  status,
  expiresAt,
}: {
  status: P2POrder['status']
  expiresAt?: string | Date
}) {
  // The `EXPIRED` DB status only flips after the backend cron successfully
  // calls expireOrder() on-chain. If that's been delayed (e.g. relayer out
  // of gas, RPC hiccup), the row is still 'ACTIVE' but the on-chain order
  // is already past its expiry — surface this as a pending-refund state
  // so the user doesn't think the order is still live.
  let effective: P2POrder['status'] | 'EXPIRED_PENDING' = status
  if (
    (status === 'ACTIVE' || status === 'PARTIAL') &&
    expiresAt &&
    new Date(expiresAt).getTime() < Date.now()
  ) {
    effective = 'EXPIRED_PENDING'
  }

  const meta = {
    ACTIVE: { dot: 'bg-blue-400', text: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30', label: 'Activa' },
    PARTIAL: { dot: 'bg-yellow-400 animate-pulse', text: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', label: 'Parcial' },
    FILLED: { dot: 'bg-uni-500', text: 'text-uni-400', bg: 'bg-uni-500/10 border-uni-500/30', label: 'Completa' },
    CANCELLED: { dot: 'bg-gray-500', text: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/30', label: 'Cancelada' },
    EXPIRED: { dot: 'bg-orange-400', text: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30', label: 'Expirada' },
    EXPIRED_PENDING: {
      dot: 'bg-orange-400 animate-pulse',
      text: 'text-orange-300',
      bg: 'bg-orange-500/10 border-orange-500/30',
      label: 'Expirada (refund pte)',
    },
  }[effective]
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border', meta.bg, meta.text)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  )
}

function shortenId(id: string) {
  return id.length > 12 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id
}

function formatHuman(wei: string, decimals = 18, maxFrac = 4): string {
  try {
    const n = BigInt(wei)
    const div = 10n ** BigInt(decimals)
    const whole = n / div
    const frac = n % div
    const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const fracStr = frac.toString().padStart(decimals, '0').slice(0, maxFrac).replace(/0+$/, '')
    return fracStr ? `${grouped}.${fracStr}` : grouped
  } catch {
    return '0'
  }
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${m % 60}m`
  const d = Math.floor(h / 24)
  return `${d}d ${h % 24}h`
}
